import { API_BASE } from '../apiBase';
// src/contracts/api.js
// Thin axios wrapper local to the contracts module. Keeps endpoint URLs in one
// place so they can be edited without grepping across components.
import axios from 'axios';

const BASE = `${API_BASE}`;

function authHeaders() {
  return { Authorization: localStorage.getItem('token') };
}

const api = {
  // Contract CRUD
  list: (projectId) =>
    axios.get(`${BASE}/contracts/project/${projectId}`, { headers: authHeaders() }).then(r => r.data),
  get: (id) =>
    axios.get(`${BASE}/contracts/${id}`, { headers: authHeaders() }).then(r => r.data),
  create: (payload) =>
    axios.post(`${BASE}/contracts`, payload, { headers: authHeaders() }).then(r => r.data),
  update: (id, payload) =>
    axios.put(`${BASE}/contracts/${id}`, payload, { headers: authHeaders() }).then(r => r.data),
  remove: (id) =>
    axios.delete(`${BASE}/contracts/${id}`, { headers: authHeaders() }).then(r => r.data),
  setStatus: (id, status) =>
    axios.put(`${BASE}/contracts/${id}/status`, { status }, { headers: authHeaders() }).then(r => r.data),

  // Preview: render the contract PDF on demand WITHOUT uploading to Dropbox
  // or changing status. Returns the raw bytes as a Blob so the caller can
  // open it in a new tab. Backed by GET /contracts/:id/render.
  previewPdf: (id) =>
    axios.get(`${BASE}/contracts/${id}/render`, {
      headers: authHeaders(),
      responseType: 'blob',
    }).then(r => r.data),

  // Exhibit & PDF
  uploadExhibit: (contractId, exhibitId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('exhibitId', exhibitId);
    return axios.post(`${BASE}/contracts/${contractId}/upload-exhibit`, fd, {
      headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  generatePdf: (id) =>
    axios.post(`${BASE}/contracts/${id}/generate`, {}, { headers: authHeaders() }).then(r => r.data),
  uploadSigned: (contractId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${BASE}/contracts/${contractId}/upload-signed`, fd, {
      headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  // Project & customer lookup (for the picker)
  listProjects: () =>
    axios.get(`${BASE}/projects`, { headers: authHeaders() }).then(r => r.data),
  getProject: (id) =>
    axios.get(`${BASE}/projects/${id}`, { headers: authHeaders() }).then(r => r.data),

  // Company settings
  companySettings: () =>
    axios.get(`${BASE}/company-settings`, { headers: authHeaders() }).then(r => r.data),

  // Resolve a Dropbox path to a temporary download URL.
  // Mirrors the helper used by ExpenseLedger.js so we use the same endpoint.
  getDropboxLink: (path) =>
    axios
      .get(`${BASE}/projects/files/link`, { headers: authHeaders(), params: { path } })
      .then(r => r.data),
};

// Fetch a PDF that lives in Dropbox and force a browser download.
// Returns the blob URL (so the caller can also open it in a new tab).
export async function downloadDropboxPdf(dropboxPath, fileName) {
  const { link } = await api.getDropboxLink(dropboxPath);
  if (!link) throw new Error('Could not get download link');
  const res = await fetch(link);
  if (!res.ok) throw new Error('Failed to fetch PDF: ' + res.status);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName || 'contract.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Keep the blob URL alive briefly so the caller can also open it in a tab.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  return blobUrl;
}

export default api;
